import React, { useRef, useState } from "react";
import { Camera, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const MAX_BYTES = 5 * 1024 * 1024;
const FORMAT_ERROR = "Only JPEG, PNG, HEIC or WEBP images are allowed.";
const OK_TYPES = [
  "image/jpeg", "image/jpg", "image/pjpeg", "image/png", "image/webp",
  "image/heic", "image/heif", "image/heic-sequence", "image/heif-sequence",
];
const HEIF_BRANDS = ["heic", "heix", "hevc", "hevx", "heim", "heis", "hevm", "hevs", "mif1", "msf1", "heif"];

// Detect the real format from the first bytes (used when the browser gives no/unknown type).
const sniffIsAllowed = async (file) => {
  const b = new Uint8Array(await file.slice(0, 16).arrayBuffer());
  const ascii = (from, to) => String.fromCharCode(...b.slice(from, to));
  if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return true; // JPEG
  if (b[0] === 0x89 && ascii(1, 4) === "PNG") return true; // PNG
  if (ascii(0, 4) === "RIFF" && ascii(8, 12) === "WEBP") return true; // WEBP
  return ascii(4, 8) === "ftyp" && HEIF_BRANDS.includes(ascii(8, 12).toLowerCase()); // HEIC/HEIF
};

const isAllowed = async (file) => {
  const type = (file.type || "").toLowerCase();
  // Missing/unknown type (common on iPhone Safari): trust the actual bytes.
  return OK_TYPES.includes(type) || sniffIsAllowed(file);
};

// "Add/Change photo" + "Remove" controls for PUT/DELETE {endpoint}.
export const PhotoUploadButton = ({ endpoint, hasPhoto, onChange, testIdPrefix }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_BYTES) return toast.error("Photo is too large. Maximum size is 5 MB.");
    if (!(await isAllowed(file))) return toast.error(FORMAT_ERROR);
    const form = new FormData();
    form.append("file", file);
    setBusy(true);
    try {
      const r = await api.put(endpoint, form, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.photo_url);
      toast.success("Photo updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not upload photo");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const r = await api.delete(endpoint);
      onChange(r.data.photo_url);
      toast.success("Photo removed");
    } catch {
      toast.error("Could not remove photo");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="inline-flex items-center gap-3 text-xs font-semibold whitespace-nowrap">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/heic-sequence,image/heif-sequence,.jpg,.jpeg,.png,.webp,.heic,.heif"
        className="hidden"
        onChange={upload}
        data-testid={`${testIdPrefix}-photo-input`}
      />
      <button
        type="button"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        data-testid={`${testIdPrefix}-photo-btn`}
        className="inline-flex items-center gap-1 text-brand-forest hover:text-brand-forest-dark disabled:opacity-50 transition-colors"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
        {hasPhoto ? "Change photo" : "Add photo"}
      </button>
      {hasPhoto && (
        <button
          type="button"
          disabled={busy}
          onClick={remove}
          data-testid={`${testIdPrefix}-photo-remove-btn`}
          className="inline-flex items-center gap-1 text-brand-ink/50 hover:text-red-500 disabled:opacity-50 transition-colors"
        >
          <X className="w-3.5 h-3.5" /> Remove
        </button>
      )}
    </div>
  );
};
